/**
 * German render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'de')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const germanLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'defokussieren' },
    change: { primary: 'ändern' },
    click: { primary: 'klick' },
    dblclick: { primary: 'doppelklick' },
    focus: { primary: 'fokus' },
    input: { primary: 'eingabe' },
    keydown: { primary: 'taste unten' },
    keypress: { primary: 'tastedrücken' },
    keyup: { primary: 'taste oben' },
    load: { primary: 'laden' },
    mousedown: { primary: 'mousedown' },
    mouseenter: { primary: 'mauseintreten' },
    mouseleave: { primary: 'mausverlassen' },
    mousemove: { primary: 'mausbewegen' },
    mouseout: { primary: 'maus weg' },
    mouseover: { primary: 'maus drüber' },
    mouseup: { primary: 'mouseup' },
    reset: { primary: 'zurücksetzen' },
    resize: { primary: 'größenänderung' },
    scroll: { primary: 'scrollen' },
    submit: { primary: 'absenden' },
    touchcancel: { primary: 'berührungabbrechen' },
    touchend: { primary: 'berührungend' },
    touchmove: { primary: 'berührungbewegen' },
    touchstart: { primary: 'berührungstart' },
    unload: { primary: 'entladen' },
  },
  logical: {
    and: { primary: 'und' },
    changes: { primary: 'ändert' },
    contains: { primary: 'enthält' },
    else: { primary: 'sonst' },
    end: { primary: 'ende' },
    equals: { primary: 'gleicht' },
    exists: { primary: 'existiert' },
    has: { primary: 'hat' },
    have: { primary: 'habe' },
    includes: { primary: 'beinhaltet' },
    is: { primary: 'ist' },
    live: { primary: 'live' },
    matches: { primary: 'passt' },
    not: { primary: 'nicht' },
    or: { primary: 'oder' },
    otherwise: { primary: 'andernfalls' },
    then: { primary: 'dann' },
    when: { primary: 'wenn' },
    where: { primary: 'wo' },
  },
  temporal: {
    forever: { primary: 'endlos' },
    h: { primary: 'std' },
    hour: { primary: 'stunde' },
    hours: { primary: 'stunden' },
    millisecond: { primary: 'millisekunde' },
    milliseconds: { primary: 'millisekunden' },
    min: { primary: 'min' },
    minute: { primary: 'minute' },
    minutes: { primary: 'minuten' },
    ms: { primary: 'ms' },
    s: { primary: 's' },
    second: { primary: 'sekunde' },
    seconds: { primary: 'sekunden' },
    times: { primary: 'mal' },
  },
  values: {
    body: { primary: 'körper' },
    detail: { primary: 'detail' },
    document: { primary: 'dokument' },
    element: { primary: 'element' },
    event: { primary: 'ereignis' },
    false: { primary: 'falsch' },
    it: { primary: 'es' },
    its: { primary: 'sein' },
    me: { primary: 'ich' },
    my: { primary: 'mein' },
    myself: { primary: 'ich selbst' },
    null: { primary: 'null' },
    result: { primary: 'ergebnis' },
    target: { primary: 'ziel' },
    true: { primary: 'wahr' },
    undefined: { primary: 'undefiniert' },
    value: { primary: 'wert' },
    window: { primary: 'fenster' },
    you: { primary: 'du' },
    your: { primary: 'dein' },
    yourself: { primary: 'dich selbst' },
  },
  attributes: {
    attribute: { primary: 'attribut' },
    attributes: { primary: 'attribute' },
    class: { primary: 'klasse' },
    classes: { primary: 'klassen' },
    properties: { primary: 'eigenschaften' },
    property: { primary: 'eigenschaft' },
    style: { primary: 'stil' },
    styles: { primary: 'stile' },
  },
  expressions: {
    up: { primary: 'hoch' },
    down: { primary: 'runter' },
    left: { primary: 'links' },
    right: { primary: 'rechts' },
    at: { primary: 'bei' },
    back: { primary: 'zurück' },
    characters: { primary: 'Zeichen' },
    children: { primary: 'kinder' },
    closest: { primary: 'nächstgelegene' },
    elsewhere: { primary: 'außerhalb' },
    empty: { primary: 'leer' },
    'ends with': { primary: 'endet mit' },
    exclusive: { primary: 'exklusiv' },
    first: { primary: 'erste' },
    'ignoring case': { primary: 'ohne Groß-/Kleinschreibung' },
    inclusive: { primary: 'inklusiv' },
    index: { primary: 'index' },
    'joined by': { primary: 'verbunden mit' },
    last: { primary: 'letzte' },
    'mapped to': { primary: 'zugeordnet zu' },
    next: { primary: 'nächste' },
    no: { primary: 'kein' },
    parent: { primary: 'elternteil' },
    prev: { primary: 'vorh' },
    previous: { primary: 'vorherige' },
    random: { primary: 'zufällig' },
    some: { primary: 'einige' },
    'sorted by': { primary: 'sortiert nach' },
    'split by': { primary: 'geteilt durch' },
    start: { primary: 'starte' },
    catch: { primary: 'fangen' },
    finally: { primary: 'schließlich' },
    def: { primary: 'funktion' },
    'starts with': { primary: 'beginnt mit' },
    within: { primary: 'innerhalb' },
  },
};

registerLexicon('de', germanLexicon);
